import React, { useEffect, useState } from "react";
import { Link, useLocation, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Menu, Sparkles, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const LOGO_SRC = "/assets/Free__2_-removebg-preview.png";

const NAV_ITEMS = [
  { to: "/our-work",      label: "Our Work" },
  { to: "/about",         label: "About" },
  { to: "/case-studies",  label: "Case Studies" },
  { to: "/resources",     label: "Resources" },
];

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  // Track scroll for compact header state
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll when mobile menu open
  useEffect(() => {
    if (mobileMenuOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileMenuOpen]);

  return (
    <header className="fixed top-0 left-0 right-0 z-[999]" data-testid="nav">
      {/* Mobile backdrop */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden fixed inset-0 bg-black/40 backdrop-blur-sm z-[900]"
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden="true"
          />
        )}
      </AnimatePresence>

      <div className="container-page pt-3.5 pb-1.5">
        <motion.nav
          initial={false}
          animate={{
            paddingTop: scrolled ? 8 : 10,
            paddingBottom: scrolled ? 8 : 10,
          }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className={cn(
            "premium-header relative z-[1000]",
            "px-4 sm:px-6 lg:px-7",
          )}
        >
          <div className="flex items-center justify-between gap-4">
            {/* Logo */}
            <Link to="/" className="flex items-center group" aria-label="Namespace Consultants — home">
              <motion.img
                src={LOGO_SRC}
                alt="Namespace Consultants"
                className="h-10 sm:h-12 lg:h-14 w-auto max-w-[160px] object-contain transition-transform duration-300 group-hover:scale-[1.03]"
                whileHover={{ rotate: -1 }}
              />
            </Link>

            {/* Desktop Nav */}
            <ul className="hidden lg:flex items-center gap-7">
              {NAV_ITEMS.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    className={({ isActive }) => cn("nav-link", isActive && "active")}
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>

            {/* Right side */}
            <div className="flex items-center gap-3">
              <NavLink
                to="/contact"
                className={({ isActive }) =>
                  cn("hidden lg:inline-block nav-link", isActive && "active")
                }
              >
                Contact
              </NavLink>

              <Button
                asChild
                className="btn-primary hidden lg:inline-flex rounded-full px-5 h-10"
              >
                <Link to="/services" className="inline-flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Explore services
                </Link>
              </Button>

              {/* Hamburger */}
              <button
                className="lg:hidden p-2 rounded-full text-[var(--text-primary)] hover:bg-white/5 transition-colors"
                onClick={() => setMobileMenuOpen((v) => !v)}
                aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={mobileMenuOpen}
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Mobile menu */}
          <AnimatePresence>
            {mobileMenuOpen && (
              <motion.div
                key="mobile-menu"
                initial={{ opacity: 0, y: -8, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.98 }}
                transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                className="lg:hidden absolute top-[calc(100%+10px)] left-0 right-0 z-[1001]"
              >
                <div className="rounded-2xl glass border border-[color:var(--menu-border)] shadow-2xl overflow-hidden">
                  <ul className="flex flex-col p-3 text-[var(--text-primary)]">
                    {[...NAV_ITEMS, { to: "/contact", label: "Contact" }].map((item) => (
                      <li key={item.to}>
                        <NavLink
                          to={item.to}
                          onClick={() => setMobileMenuOpen(false)}
                          className={({ isActive }) =>
                            cn(
                              "flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition-colors",
                              isActive
                                ? "bg-[color:var(--brand-primary)]/15 text-[var(--text-primary)]"
                                : "hover:bg-white/5 text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
                            )
                          }
                        >
                          {item.label}
                          <ChevronRight className="w-4 h-4 opacity-60" />
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                  <div className="p-3 pt-1">
                    <Button asChild className="btn-primary w-full rounded-full h-11">
                      <Link
                        to="/services"
                        onClick={() => setMobileMenuOpen(false)}
                        className="inline-flex items-center gap-2"
                      >
                        <Sparkles className="w-4 h-4" />
                        Explore services
                      </Link>
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.nav>
      </div>
    </header>
  );
};

export default Header;
